/**
 * Number and HTML formatting shared across every view.
 *
 * Lifted verbatim out of public/index.html — same inputs, same outputs. `fmt`
 * has 100+ call sites and `numFmt` 26, so any drift here is visible everywhere
 * at once; that is exactly why they are worth testing directly.
 */

/** Money-style: always 2dp, thousands separated. 1234.5 → "1,234.50" */
export function fmt(n) {
  return parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Chart/table style: precision scales down as the number grows, because a
 * 5-figure spend does not need cents on an axis label.
 *
 * @param {number|string|null} v
 * @param {number} [dec]  force a fixed precision instead of the sliding scale
 */
export function numFmt(v, dec) {
  if (v == null || isNaN(v)) return '—';
  const d = dec != null ? dec : (v >= 100 ? 0 : v >= 10 ? 1 : 2);
  return parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/**
 * An account's daily spend limit, in that account's own currency.
 *
 * Intl throws on an unrecognised currency code, and Meta has been known to send
 * one — so the fallback is "CODE amount" rather than a crash that would take the
 * whole Connections list down with it.
 */
export function fmtDslAmount(amount, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'USD').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${String(currency || '')} ${String(amount)}`.trim();
  }
}

/**
 * Escape a string for interpolation into an innerHTML template.
 *
 * TRANSITIONAL. Every JSX view that replaces a string template makes this
 * redundant — React escapes text nodes by default. Do not add new call sites;
 * port the template to JSX instead. It stays here so the ~100 existing call
 * sites in public/index.html keep working, unchanged, until they are ported.
 *
 * Note this does NOT make a URL safe to put in an href — see safeUrl.js.
 */
export function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
